import React from 'react';
import { CheckSquare, Shield, Truck, CreditCard, FileText, Car } from 'lucide-react';

/**
 * Компонент раздела дополнительных опций ОСАГО
 */
const AdditionalOptionsSection = ({ 
  formData, 
  handleInputChange, 
  handleOptionChange,
  errors 
}) => {
  if (!formData.includeAdditionalOptions) {
    return (
      <div className="mb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center mb-2">
          <input
            type="checkbox"
            id="includeAdditionalOptions"
            name="includeAdditionalOptions"
            checked={formData.includeAdditionalOptions}
            onChange={handleInputChange}
            className="form-checkbox"
          />
          <label htmlFor="includeAdditionalOptions" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
            <Shield className="w-4 h-4 mr-1 text-emerald-500" />
            Включить дополнительные опции к ОСАГО
          </label>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 ml-6">
          Дополнительные опции расширяют стандартное покрытие ОСАГО и помогают в различных ситуациях
        </p>
      </div>
    );
  }

  return (
    <div className="mb-6 border-t border-gray-200 dark:border-gray-700 pt-4">
      <div className="flex items-center mb-2">
        <input
          type="checkbox"
          id="includeAdditionalOptions"
          name="includeAdditionalOptions"
          checked={formData.includeAdditionalOptions}
          onChange={handleInputChange}
          className="form-checkbox"
        />
        <label htmlFor="includeAdditionalOptions" className="ml-2 text-sm font-medium text-gray-700 dark:text-gray-300 flex items-center">
          <Shield className="w-4 h-4 mr-1 text-emerald-500" />
          Включить дополнительные опции к ОСАГО
        </label>
      </div>
      
      <div className="mt-4 space-y-4 bg-emerald-50 dark:bg-emerald-900/20 p-4 rounded-lg border border-emerald-100 dark:border-emerald-800 ml-6">
        <h4 className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
          <CheckSquare className="w-4 h-4 mr-2 text-emerald-500" />
          Выберите дополнительные опции
        </h4>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Расширенное покрытие */}
          <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                id="extendedCoverage"
                checked={formData.additionalOptions.extendedCoverage}
                onChange={(e) => handleOptionChange('extendedCoverage', e.target.checked)}
                className="form-checkbox"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="extendedCoverage" className="text-sm font-medium text-gray-900 dark:text-white">
                Расширенное покрытие
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Увеличивает лимит выплат по страховым случаям (+15% к стоимости полиса)
              </p>
            </div>
          </div>
          
          {/* Без франшизы */}
          <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                id="noDeductible"
                checked={formData.additionalOptions.noDeductible}
                onChange={(e) => handleOptionChange('noDeductible', e.target.checked)}
                className="form-checkbox"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="noDeductible" className="text-sm font-medium text-gray-900 dark:text-white">
                Без франшизы
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Отсутствие необходимости оплачивать часть ущерба самостоятельно (+20% к стоимости полиса)
              </p>
            </div>
          </div>
          
          {/* Услуги эвакуации */}
          <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                id="evacuationService"
                checked={formData.additionalOptions.evacuationService}
                onChange={(e) => handleOptionChange('evacuationService', e.target.checked)}
                className="form-checkbox"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="evacuationService" className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <Truck className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                Услуги эвакуации
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Бесплатная эвакуация при ДТП в пределах 50 км (+1 200 ₽)
              </p>
            </div>
          </div>
          
          {/* Аренда автомобиля */}
          <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex items-start">
            <div className="flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                id="rentalCar"
                checked={formData.additionalOptions.rentalCar}
                onChange={(e) => handleOptionChange('rentalCar', e.target.checked)}
                className="form-checkbox"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="rentalCar" className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <Car className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                Аренда автомобиля
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Предоставление подменного автомобиля на время ремонта (+3 500 ₽)
              </p>
            </div>
          </div>
          
          {/* Дистанционное оформление */}
          <div className="p-3 bg-white dark:bg-gray-800 rounded border border-gray-200 dark:border-gray-700 flex items-start col-span-full">
            <div className="flex-shrink-0 mt-0.5">
              <input
                type="checkbox"
                id="remotePolicyIssue"
                checked={formData.additionalOptions.remotePolicyIssue}
                onChange={(e) => handleOptionChange('remotePolicyIssue', e.target.checked)}
                className="form-checkbox"
              />
            </div>
            <div className="ml-3">
              <label htmlFor="remotePolicyIssue" className="text-sm font-medium text-gray-900 dark:text-white flex items-center">
                <FileText className="w-3.5 h-3.5 mr-1 text-emerald-500" />
                Дистанционное оформление
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Оформление и доставка полиса без посещения офиса страховой компании (+500 ₽)
              </p>
            </div>
          </div>
        </div>
        
        <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded text-xs text-blue-700 dark:text-blue-300 flex items-start">
          <CreditCard className="w-4 h-4 mr-2 flex-shrink-0 mt-0.5 text-blue-500" />
          <p>
            Дополнительные опции увеличивают стоимость полиса, но предоставляют расширенную защиту 
            и дополнительные услуги. Полная стоимость будет рассчитана автоматически.
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdditionalOptionsSection;